import { z } from 'zod';

import { validators, zodHelpers } from './validation';

// Validation message interface
export interface ValidationMessages {
  required: (field?: string) => string;
  email: () => string;
  phone: () => string;
  url: () => string;
  minLength: (field: string, min: number) => string;
  maxLength: (field: string, max: number) => string;
  minValue: (field: string, min: number) => string;
  maxValue: (field: string, max: number) => string;
  invalidFormat: (field: string) => string;
  tcKimlikInvalid: () => string;
  tcKimlikLength: () => string;
  taxNumberLength: () => string;
  taxNumberFormat: () => string;
  dateRequired: () => string;
  invalidDate: () => string;
  passwordMin: (min: number) => string;
  numeric: () => string;
  alphanumeric: () => string;
  usernameFormat: () => string;
  usernameLength: (min: number, max: number) => string;
  futureDate: () => string;
  pastDate: () => string;
  dateInRange: (start: string, end: string) => string;
  deliveryBeforePickup: () => string;
  customerTypeRequired: () => string;
  licenseClassRequired: () => string;
  positiveNumber: () => string;
  integerRequired: () => string;
}

// Default messages (Turkish)
export const defaultMessages: ValidationMessages = {
  required: (field) => (field ? `${field} zorunludur` : 'Bu alan zorunludur'),
  email: () => 'Geçerli bir e-posta adresi girin',
  phone: () => 'Geçerli bir telefon numarası girin',
  url: () => 'Geçerli bir URL girin',
  minLength: (field, min) => `${field} en az ${min} karakter olmalıdır`,
  maxLength: (field, max) => `${field} en fazla ${max} karakter olabilir`,
  minValue: (field, min) => `${field} ${min}'dan küçük olamaz`,
  maxValue: (field, max) => `${field} ${max}'dan büyük olamaz`,
  invalidFormat: (field) => `${field} geçerli bir formatta değil`,
  tcKimlikInvalid: () => 'Geçersiz TC Kimlik No',
  tcKimlikLength: () => 'TC Kimlik No 11 haneli olmalıdır',
  taxNumberLength: () => 'Vergi No 10 haneli olmalıdır',
  taxNumberFormat: () => 'Geçerli bir Vergi No girin',
  dateRequired: () => 'Tarih seçimi zorunludur',
  invalidDate: () => 'Geçerli bir tarih seçin',
  passwordMin: (min) => `Şifre en az ${min} karakter olmalıdır`,
  numeric: () => 'Sadece rakam kullanabilirsiniz',
  alphanumeric: () => 'Sadece harf ve rakam kullanabilirsiniz',
  usernameFormat: () => 'Kullanıcı adı sadece harf, rakam, tire ve alt çizgi içerebilir',
  usernameLength: (min, max) => `Kullanıcı adı ${min}-${max} karakter arasında olmalıdır`,
  futureDate: () => 'Gelecek bir tarih seçmelisiniz',
  pastDate: () => 'Geçmiş bir tarih seçmelisiniz',
  dateInRange: (start, end) => `Tarih ${start} ile ${end} arasında olmalıdır`,
  deliveryBeforePickup: () => 'Teslim tarihi alım tarihinden önce olamaz',
  customerTypeRequired: () => 'Müşteri tipi seçiniz',
  licenseClassRequired: () => 'Ehliyet sınıfı seçiniz',
  positiveNumber: () => 'Pozitif bir sayı girin',
  integerRequired: () => 'Tam sayı giriniz',
};

// Create localized field builders
export function createFieldBuilders(messages: ValidationMessages = defaultMessages) {
  return {
    // Email field with optional custom message
    email: (customMessage?: string) =>
      z.string().email({ message: customMessage || messages.email() }),

    // Phone field with Turkish format
    phone: (required = false) => {
      const schema = z.string().regex(/^(\+90|0)?[1-9][0-9]{9}$/, messages.phone());
      return required ? schema : schema.optional();
    },

    // TCKN field with full validation
    tcKimlik: (required = false) => {
      const schema = z
        .string()
        .length(11, messages.tcKimlikLength())
        .regex(/^[1-9][0-9]{10}$/, messages.tcKimlikInvalid())
        .refine((value) => validators.tcKimlikNo(value), messages.tcKimlikInvalid());
      return required ? schema : schema.optional();
    },

    // Tax number field
    taxNumber: (required = false) => {
      const schema = z
        .string()
        .length(10, messages.taxNumberLength())
        .regex(/^[0-9]{10}$/, messages.taxNumberFormat());
      return required ? schema : schema.optional();
    },

    // Date range fields
    dateRange: (fieldNames = { start: 'startDate', end: 'endDate' }) => ({
      [fieldNames.start]: z.date({
        message: messages.dateRequired(),
      }),
      [fieldNames.end]: z.date({
        message: messages.dateRequired(),
      }),
    }),

    // Required text field
    requiredText: (fieldName: string, min = 1, max?: number) => {
      let schema = z.string().min(min, messages.required(fieldName));
      if (max) {
        schema = schema.max(max, messages.maxLength(fieldName, max));
      }
      return schema;
    },

    // Optional text field
    optionalText: (max?: number) => {
      let schema = zodHelpers.optionalString();
      if (max) {
        schema = z
          .string()
          .max(max, messages.maxLength('', max))
          .trim()
          .transform((val) => (val === '' ? undefined : val))
          .optional();
      }
      return schema;
    },

    // URL field
    url: (required = false) => {
      const schema = z.string().url({ message: messages.url() });
      return required ? schema : schema.optional();
    },

    // Number field with range
    numberInRange: (min: number, max: number, fieldName: string) =>
      z
        .number({
          message: messages.required(fieldName),
        })
        .min(min, messages.minValue(fieldName, min))
        .max(max, messages.maxValue(fieldName, max)),

    // Percentage field
    percentage: (fieldName = 'Yüzde') =>
      z
        .number()
        .min(0, messages.minValue(fieldName, 0))
        .max(100, messages.maxValue(fieldName, 100)),

    // Username field
    username: () =>
      z
        .string()
        .min(3, messages.usernameLength(3, 20))
        .max(20, messages.usernameLength(3, 20))
        .regex(/^[a-zA-Z0-9_-]+$/, messages.usernameFormat()),

    // Numeric string
    numeric: () => z.string().regex(/^[0-9]+$/, messages.numeric()),

    // Alphanumeric string
    alphanumeric: () => z.string().regex(/^[a-zA-Z0-9]+$/, messages.alphanumeric()),

    // Future date
    futureDate: () => z.date().refine((date) => date > new Date(), messages.futureDate()),

    // Past date
    pastDate: () => z.date().refine((date) => date < new Date(), messages.pastDate()),

    // Date in range
    dateInRange: (min: Date, max: Date) =>
      z
        .date()
        .refine(
          (date) => date >= min && date <= max,
          messages.dateInRange(min.toLocaleDateString('tr-TR'), max.toLocaleDateString('tr-TR'))
        ),

    // Password field
    password: (min = 6) => z.string().min(min, messages.passwordMin(min)),

    // Positive number
    positiveNumber: () => z.number().positive(messages.positiveNumber()),

    // Integer
    integer: () => z.number().int(messages.integerRequired()),
  };
}

// Create localized common schemas
export function createCommonSchemas(messages: ValidationMessages = defaultMessages) {
  const fieldBuilders = createFieldBuilders(messages);

  return {
    // Login form schema
    login: z.object({
      email: fieldBuilders.email(),
      password: fieldBuilders.password(6),
      rememberMe: z.boolean().optional(),
    }),

    // Customer form schema
    customer: z.object({
      name: fieldBuilders.requiredText('İsim'),
      companyName: fieldBuilders.requiredText('Firma adı'),
      email: fieldBuilders.email(),
      phone: fieldBuilders.phone(false),
      address: fieldBuilders.requiredText('Adres'),
      type: z.enum(['corporate', 'individual'], {
        message: messages.customerTypeRequired(),
      }),
      taxNumber: fieldBuilders.taxNumber(false),
      taxOffice: fieldBuilders.optionalText(50),
    }),

    // Driver form schema
    driver: z.object({
      firstName: fieldBuilders.requiredText('Ad'),
      lastName: fieldBuilders.requiredText('Soyad'),
      identityNumber: fieldBuilders.tcKimlik(true),
      phone: fieldBuilders.phone(true),
      birthDate: z.date({
        message: messages.dateRequired(),
      }),
      licenseNumber: z.string().min(1, messages.required('Ehliyet numarası')),
      licenseClass: z.enum(['B', 'C', 'CE', 'D', 'DE'], {
        message: messages.licenseClassRequired(),
      }),
    }),

    // Order form schema with date validation
    order: (() => {
      const baseSchema = z.object({
        customerId: z.string().min(1, messages.required('Müşteri')),
        pickupLocation: fieldBuilders.requiredText('Alım yeri'),
        deliveryLocation: fieldBuilders.requiredText('Teslim yeri'),
        pickupDate: z.date({
          message: messages.dateRequired(),
        }),
        deliveryDate: z.date({
          message: messages.dateRequired(),
        }),
        cargo: fieldBuilders.requiredText('Kargo'),
        weight: fieldBuilders.numberInRange(0.1, 100000, 'Ağırlık'),
        quantity: fieldBuilders.integer().positive(messages.positiveNumber()),
        price: fieldBuilders.positiveNumber(),
        specialInstructions: fieldBuilders.optionalText(500),
      });

      return baseSchema.refine(
        (data) => {
          if (data.pickupDate && data.deliveryDate) {
            return data.deliveryDate >= data.pickupDate;
          }
          return true;
        },
        {
          message: messages.deliveryBeforePickup(),
          path: ['deliveryDate'],
        }
      );
    })(),
  };
}

// Re-export utilities from main validation file
export { validators, zodHelpers } from './validation';

// Export main factory with default messages
export const fieldBuilders = createFieldBuilders(defaultMessages);
export const commonSchemas = createCommonSchemas(defaultMessages);
